import { Injectable } from "@angular/core";
import {
  Headers,
  Http,
  RequestOptions,
  RequestOptionsArgs,
  Response,
  ResponseContentType,
  XHRBackend,
} from "@angular/http";
import "rxjs/add/operator/catch";
import { RSS_AUTH_CLIENT_URL } from "../../config/auth-config";
import { ERROR } from "../constant";
import { AuthService } from "./auth.service";

export interface CustomWindow extends Window {
  BrowserSecurity: any;
}
declare const window: CustomWindow;

// SLACKERS-595: the UNKNOWN error page is the catch-all for every failure that
// isn't a recognised HTTP status, so on its own it tells us nothing about what
// broke. Describe the failure well enough to tell the three cases apart:
// an unexpected HTTP status, a transport failure (status 0 — aborted, CORS,
// offline), and a JSON parse error on an otherwise-successful response.
function describeFailure(err): string {
  const hasStatus = err.status !== undefined && err.status !== null;
  let kind: string;

  if (err.status) {
    kind = 'http';
  } else if (err.name === 'SyntaxError') {
    kind = 'parse';
  } else {
    kind = 'transport';
  }

  const status = hasStatus ? String(err.status) : 'none';

  return [
    `status=${encodeURIComponent(status)}`,
    `kind=${kind}`,
    `from=${encodeURIComponent(window.location.pathname)}`,
  ].join('&');
}

@Injectable()
export class HttpService {
  constructor(
    private http: Http,
    private backend: XHRBackend,
    private options: RequestOptions,
    private authService: AuthService,
  ) {}

  async head(url: string, options?: RequestOptionsArgs): Promise<Response> {
    return this.http
      .head(url, await this.getRequestOptionArgs(options))
      .toPromise();
  }

  async request(url: string, options?: RequestOptionsArgs): Promise<Response> {
    return this.http
      .head(url, await this.getRequestOptionArgs(options))
      .toPromise();
  }

  async get(url: string, options?: RequestOptionsArgs): Promise<Response> {
    return this.http
      .get(url, await this.getRequestOptionArgs(options))
      .toPromise()
      .then((data) => Promise.resolve(data.text() === "" ? data : data.json()))
      .catch(this.handleError);
  }

  async post(
    url: string,
    body: any,
    options?: RequestOptionsArgs,
  ): Promise<Response | Blob> {
    const requestOption = await this.getRequestOptionArgs(options);
    return this.http
      .post(url, body, requestOption)
      .toPromise()
      .then((data) => {
        if (data) {
          return Promise.resolve(
            requestOption &&
              requestOption.responseType === ResponseContentType.Blob
              ? data.blob()
              : data.json(),
          );
        }
        return Promise.resolve(data);
      })
      .catch(this.handleError);
  }

  async put(
    url: string,
    body: any,
    options?: RequestOptionsArgs,
  ): Promise<Response> {
    return this.http
      .put(url, body, await this.getRequestOptionArgs(options))
      .toPromise()
      .then((data) => Promise.resolve(data.text() === "" ? data : data.json()))
      .catch(this.handleError);
  }

  async delete(url: string, options?: RequestOptionsArgs): Promise<Response> {
    return this.http
      .delete(url, await this.getRequestOptionArgs(options))
      .toPromise()
      .then((data) => Promise.resolve(data.text() === "" ? data : data.json()))
      .catch(this.handleError);
  }

  async getRequestOptionArgs(
    optionArgs?: RequestOptionsArgs,
  ): Promise<RequestOptionsArgs> {
    // const token = await this.authService.getToken();

    // if (token) {
    const options: RequestOptionsArgs = new RequestOptions();

    if (!optionArgs || (optionArgs && !optionArgs.headers)) {
      options.headers = new Headers();
    }

    const spoofUser = this.authService.getSpoofUser();
    if (spoofUser) {
      options.headers.set("impersonate-id", spoofUser.userId);
    }

    options.withCredentials = true;

    if (window.navigator.userAgent.search(/MSIE|Trident/i) != -1) {
      options.headers.set("Content-Type", "application/json");
      options.headers.set("Cache-control", "no-cache, no-store");
      options.headers.set("Pragma", "no-cache");
      options.headers.set("Access-Control-Allow-Origin", "*");
      options.headers.set("Access-Control-Allow-Headers", "*");
    }

    if (optionArgs) {
      if (optionArgs.headers && optionArgs.headers.get("Content-Type")) {
        options.headers.set(
          "Content-Type",
          optionArgs.headers.get("Content-Type"),
        );
      }

      if (optionArgs.responseType) {
        options.responseType = optionArgs.responseType;
      }

      if (optionArgs.body) {
        options.body = optionArgs.body || {};
      }

      if (optionArgs.params) {
        options.params = optionArgs.params;
      }
    }

    return options;
  }

  handleError(err) {
    if (err) {
      if (err.status === 401) {
        const defaultLocationUtils = {
          assign: (url: string) => window.location.assign(url),
          get href() {
            return window.location.href;
          },
        };
        const browserSecurity = new window.BrowserSecurity(
          defaultLocationUtils,
          RSS_AUTH_CLIENT_URL,
        );
        browserSecurity.login();
      } else if (err.status === 403) {
        window.location.href = ERROR.ACCESS_DENIED.url;
      } else if (err.status === 404) {
        window.location.href = ERROR.NOT_FOUND.url;
      } else if (err.status === 503) {
        window.location.href = ERROR.SERVICE_UNAVAILABLE.url;
      } else {
        window.location.href = `${ERROR.UNKNOWN.url}?${describeFailure(err)}`;
      }
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/slipresistant/slipresistant/client/src/app/shared/service/http.service.ts